import React, { useState } from 'react';
import PayButton from '../../../components/shared/payButton/PayButton';
import ImageModal from '../../../components/shared/ImageModal/ImageModal';
import { useCart } from '../../../context/CartContext';

export const CardDetailsContent = ({ event, userId }) => {
  const { cartItems, addToCart } = useCart();
  const [ticketCounts, setTicketCounts] = useState({});
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Image modal state
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState(null);
  const [modalAlt, setModalAlt] = useState('');

  const openImageModal = (src, alt) => {
    setModalImageSrc(src);
    setModalAlt(alt || '');
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setModalImageSrc(null);
    setModalAlt('');
  };

  const WORD_LIMIT = 60;

  const truncateHtml = (html, limit) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    let count = 0;
    let finished = false;

    const traverse = (node) => {
      if (finished) return null;

      if (node.nodeType === Node.TEXT_NODE) {
        const words = node.textContent.split(/\s+/).filter(w => w.length > 0);
        if (count + words.length > limit) {
          const remaining = limit - count;
          node.textContent = words.slice(0, remaining).join(' ') + '...';
          count = limit;
          finished = true;
        } else {
          count += words.length;
        }
        return node;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const newNode = document.createElement(node.tagName);
        // Copy attributes (styles, classes, etc.)
        Array.from(node.attributes).forEach(attr => {
          newNode.setAttribute(attr.name, attr.value);
        });

        for (const child of Array.from(node.childNodes)) {
          const newChild = traverse(child);
          if (newChild) {
            newNode.appendChild(newChild);
          }
          if (finished) break;
        }
        return newNode;
      }
      return null;
    };

    const fragment = document.createDocumentFragment();
    for (const child of Array.from(doc.body.childNodes)) {
      const newChild = traverse(child);
      if (newChild) fragment.appendChild(newChild);
      if (finished) break;
    }

    const div = document.createElement('div');
    div.appendChild(fragment);
    return div.innerHTML;
  };

  const getDescriptionText = () => {
    const description = event?.description || '';
    
    if (isDescriptionExpanded) {
      return description;
    }
    
    const doc = new DOMParser().parseFromString(description, 'text/html');
    const strippedDescription = doc.body.textContent || '';
    const words = strippedDescription.split(/\s+/).filter(w => w.length > 0);
    
    if (words.length <= WORD_LIMIT) {
      return description;
    }
    
    return truncateHtml(description, WORD_LIMIT);
  };

  const shouldShowReadMore = () => {
    const description = event?.description || '';
    const doc = new DOMParser().parseFromString(description, 'text/html');
    const strippedDescription = doc.body.textContent || '';
    const words = strippedDescription.split(/\s+/).filter(w => w.length > 0);
    return words.length > WORD_LIMIT;
  };

  const handleIncrement = (ticketName, available) => {
    const currentCount = ticketCounts[ticketName] || 1;
    if (currentCount < available) {
      setTicketCounts({ ...ticketCounts, [ticketName]: currentCount + 1 });
    }
  };

  const handleDecrement = (ticketName) => {
    const currentCount = ticketCounts[ticketName] || 1;
    if (currentCount > 1) {
      setTicketCounts({ ...ticketCounts, [ticketName]: currentCount - 1 });
    }
  };

  // Helper to find ticket ID from the tickets array since ticket_availability might not have it
  const getTicketId = (ticketName) => {
    const ticket = event?.tickets?.find(t => t.ticketName === ticketName);
    return ticket?._id;
  };

  const handleAddToCart = (ticket, count, targetMonth = null) => {
    const ticketId = getTicketId(ticket.ticketName);
    addToCart({
      eventId: event._id,
      ticketId: ticketId,
      ticketsCount: count,
      eventName: event.title,
      ticketName: ticket.ticketName,
      ticketPrice: ticket.ticketPrice,
      platformFee: ticket.platformFee,
      eventData: event,
      targetMonth
    });
  };

  return (
    <div className="section-home-site_content">
      <div className="page-padding-2">
        <div className="container-medium">
          <div className="padding-vertical padding-small">
            <div className="home-site_component">
              <div 
                data-w-id="4db98acf-151e-b8b5-28c8-d03c9b1db089" 
                style={{ opacity: 1 }} 
                className="home-site_flex-items"
              >
                <div className="site_left-item">
                  <div className="site_flex-block">
                    <div className="site_flex-left-content">
                      <p className="category-listing-page">{event?.category?.service_name}</p>
                      <div className="margin-bottom margin-xsmall">
                        <h2 className="heading-7">{event?.organizerId?.full_name}</h2>
                      </div>
                      <div className="margin-bottom">
                        <div className="text-weight-bold">
                          <span dangerouslySetInnerHTML={{ __html: getDescriptionText() }} />
                          {shouldShowReadMore() && (
                            <span
                              onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                              style={{
                                color: '#007bff',
                                cursor: 'pointer',
                                marginLeft: '5px',
                                fontWeight: 'normal'
                              }}
                            >
                              {isDescriptionExpanded ? 'Read less' : 'Read more'}
                            </span>
                          )}
                          <br />
                        </div>
                      </div>
                    </div>
                    <div className="review-five-user hide-on-mob">
                      <img src={event?.organizerId?.profile_pic} alt="" className="listing-page-user-dp" /></div>
                  </div>
                  <div className="site_content-divider"></div>
                  <div className="site_flex-left-content">
                    <div className="margin-bottom margin-xsmall">
                      <h2 className="heading-7">Price</h2>
                    </div>
                  </div>
                  {event?.ticket_availability?.map((ticket, index) => {
                    const count = ticketCounts[ticket.ticketName] || 1;
                    const ticketId = getTicketId(ticket.ticketName);
                    const isSoldOut = ticket.available === 0;
                    const isLowStock = ticket.available > 0 && ticket.available <= 5;
                    const isRoutine = event?.type === 'Routine';
                    const showRenew = isRoutine && event?.routineRenewEligible && event?.routineNextMonth;
                    const targetMonth = showRenew ? event?.routineNextMonth : null;

                    return (
                      <div key={index} className="card_content-block" style={{ position: 'relative' }}>
                        <div className="card-flex-block">
                          <div className="rate-div">
                            <div className="night-price-text">{ticket.ticketName}</div>
                            <div className="night-price-text">
                              <span className="price-text">₹{ticket?.gst_amount > 0 ? ticket.ticketPrice - ticket?.gst_amount : ticket.ticketPrice} </span>/ per seat {
                                ticket?.gst_amount > 0 && (
                                  <span>+ 18% GST</span>
                                )
                              } <span>+ ₹{ticket?.platformFee} platform fee</span>
                            </div>
                            {isLowStock && (
                              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px', fontWeight: 'bold' }}>
                                Only {ticket.available} left!
                              </div>
                            )}
                            {isSoldOut && (
                              <div style={{ color: '#ff4d4f', fontSize: '14px', marginTop: '4px', fontWeight: 'bold' }}>
                                SOLD OUT
                              </div>
                            )}
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            {!isSoldOut && (
                              <div className="quantity-selector" style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                border: '1px solid #ddd', 
                                borderRadius: '25px', 
                                padding: '2px 8px',
                                background: '#f9f9f9'
                              }}>
                                <button 
                                  onClick={() => handleDecrement(ticket.ticketName)}
                                  style={{ 
                                    background: 'none', 
                                    border: 'none', 
                                    cursor: 'pointer', 
                                    fontSize: '18px', 
                                    padding: '5px 10px',
                                    color: count > 1 ? '#333' : '#ccc'
                                  }}
                                  disabled={count <= 1}
                                >-</button>
                                <span style={{ minWidth: '20px', textAlign: 'center', fontWeight: 'bold' }}>{count}</span>
                                <button 
                                  onClick={() => handleIncrement(ticket.ticketName, ticket.available)}
                                  style={{ 
                                    background: 'none', 
                                    border: 'none', 
                                    cursor: 'pointer', 
                                    fontSize: '18px', 
                                    padding: '5px 10px',
                                    color: count < ticket.available ? '#333' : '#ccc'
                                  }}
                                  disabled={count >= ticket.available}
                                >+</button>
                              </div>
                            )}
                            
                                                        {!isSoldOut && (
                              <button 
                                onClick={() => handleAddToCart(ticket, count, targetMonth)}
                                className="button-3 w-button"
                                style={{
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  border: '1px solid #ddd', 
                                  borderRadius: '25px', 
                                  padding: cartItems.some(item => item.ticketId === ticketId && (item.targetMonth || null) === targetMonth) ? '9px 9px' : '9px 9px',
                                  background: cartItems.some(item => item.ticketId === ticketId && (item.targetMonth || null) === targetMonth) ? '#b03052' : '#f9f9f9',
                                  color: cartItems.some(item => item.ticketId === ticketId && (item.targetMonth || null) === targetMonth) ? 'white' : 'black',
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                {cartItems.some(item => item.ticketId === ticketId && (item.targetMonth || null) === targetMonth) ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-cart-check" viewBox="0 0 16 16">
  <path d="M11.354 6.354a.5.5 0 0 0-.708-.708L8 8.293 6.854 7.146a.5.5 0 1 0-.708.708l1.5 1.5a.5.5 0 0 0 .708 0z"/>
  <path d="M.5 1a.5.5 0 0 0 0 1h1.11l.401 1.607 1.498 7.985A.5.5 0 0 0 4 12h1a2 2 0 1 0 0 4 2 2 0 0 0 0-4h7a2 2 0 1 0 0 4 2 2 0 0 0 0-4h1a.5.5 0 0 0 .491-.408l1.5-8A.5.5 0 0 0 14.5 3H2.89l-.405-1.621A.5.5 0 0 0 2 1zm3.915 10L3.102 4h10.796l-1.313 7zM6 14a1 1 0 1 1-2 0 1 1 0 0 1 2 0m7 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
</svg>
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-bag-plus" viewBox="0 0 16 16">
                                    <path d="M9 5.5a.5.5 0 0 0-1 0V7H6.5a.5.5 0 0 0 0 1H8v1.5a.5.5 0 0 0 1 0V8h1.5a.5.5 0 0 0 0-1H9z"/>
                                    <path d="M.5 1a.5.5 0 0 0 0 1h1.11l.401 1.607 1.498 7.985A.5.5 0 0 0 4 12h1a2 2 0 1 0 0 4 2 2 0 0 0 0-4h7a2 2 0 1 0 0 4 2 2 0 0 0 0-4h1a.5.5 0 0 0 .491-.408l1.5-8A.5.5 0 0 0 14.5 3H2.89l-.405-1.621A.5.5 0 0 0 2 1zm3.915 10L3.102 4h10.796l-1.313 7zM6 14a1 1 0 1 1-2 0 1 1 0 0 1 2 0m7 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
                                  </svg>
                                )}
                              </button>
                            )} 
                            <PayButton
                              userId={userId}
                              eventId={event?._id}
                              ticketId={ticketId}
                              ticketsCount={count}
                              initialPrice={(ticket.ticketPrice * count)+ ticket?.platformFee}
                              eventName={event?.title}
                              onBookingSuccess={(data) => console.log('Booking success:', data)}
                              onBookingFailure={(error) => console.log('Booking failure:', error)}
                              disabled={isSoldOut}
                              isSoldOutText={isSoldOut ? 'Sold Out' : null}
                              platformfee={ticket?.platformFee}
                              eventData={event}
                              targetMonth={targetMonth}
                              buttonLabel={showRenew ? 'Renew' : 'Reserve'}
                            />


                          </div>
                          
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div id="w-node-_4db98acf-151e-b8b5-28c8-d03c9b1db0c5-d3b03550" className="site_right-item">
                  {(() => {
                    // Parse the supportingImages - it may contain stringified JSON arrays
                    let images = [];
                    if (event?.supportingImages?.length > 0) {
                      event.supportingImages.forEach((item) => {
                        try {
                          // Check if item is a stringified array and parse it
                          const parsed = JSON.parse(item);
                          if (Array.isArray(parsed)) {
                            images = [...images, ...parsed];
                          } else {
                            images.push(parsed);
                          }
                        } catch {
                          // If parsing fails, it's a plain URL string
                          images.push(item);
                        }
                      });
                    }
                    
                    return images.map((image, index) => {
                      const positionClass = index < 4 ? ` _0${index + 1}` : '';
                      return (
                        <div 
                          key={index} 
                          className={`card_content-block imageblock${positionClass}`}
                          
                        >
                          <img 
                            src={image} 
                            loading="lazy" 
                            alt={`Supporting image ${index + 1}`} 
                            className="image-height" 
                            style={{ cursor: 'pointer' }}
                            tabIndex={0}
                            onClick={() => openImageModal(image, `Supporting image ${index + 1}`)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') openImageModal(image, `Supporting image ${index + 1}`);
                            }}
                          />
                        </div>
                      );
                    });
                  })()}
                </div>
                <ImageModal
                  isOpen={isImageModalOpen}
                  onClose={closeImageModal}
                  imageSrc={modalImageSrc}
                  alt={modalAlt}
                />
              </div>
              <div className="site_content-divider"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
